import {
  Button,
  Checkbox,
  DatePicker,
  FormField,
  Input,
  SegmentedControl,
  Select,
  Text,
  Textarea,
  todayIso,
  useToast,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { errorMessage, formErrors } from '../../../lib/errors';
import { formatDate } from '../../../lib/format';
import {
  useCreateMarket,
  useScheduleDates,
  useUpdateMarket,
  type Market,
  type Schedule,
} from '../api';
import styles from '../pages/SalesPages.module.css';

type Kind = Schedule['kind'];
const WEEK = [1, 2, 3, 4, 5, 6, 7];
const PREVIEW_LIMIT = 8;

/** Creates or changes a market with its schedule (MKT-003). */
export function MarketDialog({
  market,
  onClose,
  onSaved,
}: {
  market?: Market;
  onClose: () => void;
  onSaved?: (market: Market) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const create = useCreateMarket();
  const update = useUpdateMarket(market?.id ?? '', market?.version);
  const save = market ? update : create;
  const [name, setName] = useState(market?.name ?? '');
  const [place, setPlace] = useState(market?.place ?? '');
  const [notes, setNotes] = useState(market?.notes ?? '');
  const [kind, setKind] = useState<Kind>(market?.schedule.kind ?? 'single');
  const [from, setFrom] = useState(market?.schedule.from ?? todayIso());
  const [to, setTo] = useState(market?.schedule.to ?? market?.schedule.from ?? todayIso());
  const [weekdays, setWeekdays] = useState<number[]>(market?.schedule.weekdays ?? []);
  const [everyWeeks, setEveryWeeks] = useState(market?.schedule.every_weeks ?? 1);
  const [exceptions, setExceptions] = useState<string[]>(market?.schedule.exceptions ?? []);
  const [exception, setException] = useState('');
  const [startsAt, setStartsAt] = useState(market?.starts_at?.slice(0, 5) ?? '');
  const [endsAt, setEndsAt] = useState(market?.ends_at?.slice(0, 5) ?? '');

  const schedule: Schedule =
    kind === 'single'
      ? { kind, from, weekdays: [], exceptions: [] }
      : kind === 'range'
        ? { kind, from, to, weekdays, exceptions: [] }
        : { kind, from, to, weekdays, every_weeks: everyWeeks, exceptions };
  const preview = useScheduleDates(schedule);
  const errors = formErrors(save.error, {
    name: ['market.name'],
    times: ['market.times'],
  });
  const toggleDay = (day: number) =>
    setWeekdays((w) => (w.includes(day) ? w.filter((d) => d !== day) : [...w, day]));
  const dates = preview.data ?? [];
  const short = t('sales.markets.weekdayShort', { returnObjects: true }) as string[];

  return (
    <FormDialog
      open
      onClose={onClose}
      title={market ? t('sales.markets.editTitle') : t('sales.markets.createTitle')}
      submitLabel={market ? t('common.save') : t('sales.markets.create')}
      pending={save.isPending}
      error={errors.general}
      size="lg"
      onSubmit={() =>
        save.mutate(
          {
            name,
            place: place || null,
            notes: notes || null,
            schedule,
            starts_at: startsAt ? `${startsAt}:00` : null,
            ends_at: endsAt ? `${endsAt}:00` : null,
          },
          {
            onSuccess: (m) => {
              toast.show(
                market ? t('sales.markets.saved') : t('sales.markets.created', { name: m.name }),
              );
              onSaved?.(m);
              onClose();
            },
          },
        )
      }
    >
      <FormField label={t('sales.markets.name')} required error={errors.field.name}>
        {(p) => (
          <Input
            {...p}
            value={name}
            placeholder={t('sales.markets.namePlaceholder')}
            onChange={(e) => setName(e.target.value)}
          />
        )}
      </FormField>
      <FormField label={t('sales.markets.place')}>
        {(p) => <Input {...p} value={place} onChange={(e) => setPlace(e.target.value)} />}
      </FormField>
      <SegmentedControl
        label={t('sales.markets.kind')}
        value={kind}
        onChange={setKind}
        fullWidth
        options={[
          { value: 'single', label: t('sales.markets.kinds.single') },
          { value: 'range', label: t('sales.markets.kinds.range') },
          { value: 'recurring', label: t('sales.markets.kinds.recurring') },
        ]}
      />
      <div className={styles.row}>
        <FormField label={kind === 'single' ? t('sales.markets.date') : t('sales.markets.from')}>
          {(p) => (
            <DatePicker
              {...p}
              value={from}
              onChange={(v) => {
                setFrom(v);
                if (to < v) setTo(v);
              }}
            />
          )}
        </FormField>
        {kind !== 'single' && (
          <FormField label={t('sales.markets.to')}>
            {(p) => <DatePicker {...p} value={to} onChange={setTo} />}
          </FormField>
        )}
      </div>
      {kind !== 'single' && (
        <fieldset className={styles.weekdays}>
          <legend>{t('sales.markets.weekdays')}</legend>
          <div className={styles.weekdayRow}>
            {WEEK.map((day) => (
              <Checkbox
                key={day}
                label={short[day - 1]}
                checked={weekdays.includes(day)}
                onChange={() => toggleDay(day)}
              />
            ))}
          </div>
          {kind === 'range' && (
            <Text tone="muted" size="sm">
              {t('sales.markets.weekdaysHintRange')}
            </Text>
          )}
        </fieldset>
      )}
      {kind === 'recurring' && (
        <>
          <FormField label={t('sales.markets.everyWeeks')}>
            {(p) => (
              <Select
                {...p}
                value={String(everyWeeks)}
                onChange={(e) => setEveryWeeks(Number(e.target.value))}
                options={[...new Set([1, 2, 3, 4, everyWeeks])]
                  .sort((a, b) => a - b)
                  .map((n) => ({
                    value: String(n),
                    label: t('sales.markets.every', { count: n }),
                  }))}
              />
            )}
          </FormField>
          <FormField label={t('sales.markets.exceptions')}>
            {(p) => (
              <div className={styles.exceptionAdd}>
                <DatePicker {...p} value={exception} onChange={setException} />
                <Button
                  variant="secondary"
                  disabled={!exception || exceptions.includes(exception)}
                  onClick={() => {
                    setExceptions((e) => [...e, exception].sort());
                    setException('');
                  }}
                >
                  {t('sales.markets.exceptionAdd')}
                </Button>
              </div>
            )}
          </FormField>
          {exceptions.length > 0 && (
            <ul className={styles.chips}>
              {exceptions.map((e) => (
                <li key={e}>
                  <Button
                    variant="secondary"
                    size="sm"
                    aria-label={t('sales.markets.exceptionRemove', { date: formatDate(e) })}
                    onClick={() => setExceptions((x) => x.filter((d) => d !== e))}
                  >
                    {formatDate(e)} ✕
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <div className={styles.row}>
        <FormField label={t('sales.markets.startsAt')}>
          {(p) => (
            <Input
              {...p}
              type="time"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          )}
        </FormField>
        <FormField label={t('sales.markets.endsAt')} error={errors.field.times}>
          {(p) => (
            <Input {...p} type="time" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
          )}
        </FormField>
      </div>
      <FormField label={t('sales.markets.notes')}>
        {(p) => (
          <Textarea {...p} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        )}
      </FormField>
      <section aria-live="polite" aria-label={t('sales.markets.preview')}>
        {preview.error ? (
          <Text tone="danger" size="sm" role="alert">
            {errorMessage(preview.error)}
          </Text>
        ) : (
          dates.length > 0 && (
            <Text size="sm">
              <strong>{t('sales.markets.previewCount', { count: dates.length })}</strong>
              {': '}
              {dates.slice(0, PREVIEW_LIMIT).map(formatDate).join(', ')}
              {dates.length > PREVIEW_LIMIT &&
                ` ${t('sales.markets.previewMore', { count: dates.length - PREVIEW_LIMIT })}`}
            </Text>
          )
        )}
      </section>
    </FormDialog>
  );
}
